import { ILocation } from '@/Interfaces/Forecaster';
import { STATES_REGEX, ZIP_CODES_REGEX } from '@/constants/geography';
import { handlePasteStatesAndZipCodes, handlePasteZipCodes } from '@/helpers/forecaster/geography';
import { fetchTargetingLocationsLookup } from '@/store/api/geo.api';
import { ClipboardEvent, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

const useGeoLocationsPaste = (
  selectedLocations: ILocation[],
  setSelectedLocations: (locations: ILocation[]) => void,
  reportNotFoundLocations?: (locations: ILocation[]) => void,
) => {
  const [loading, setLoading] = useState(false);

  const onValuesPaste = useCallback(
    async (event: ClipboardEvent<HTMLInputElement>) => {
      const value = (event.clipboardData || window.Clipboard).getData('text').trim();

      const payload = {
        zips: [],
        states: [],
      } as { zips: string[]; states: string[] };

      if (STATES_REGEX.test(value)) {
        const { states, zipCodes } = handlePasteStatesAndZipCodes(value, selectedLocations);
        payload.states = states;
        payload.zips = zipCodes;
      } else if (ZIP_CODES_REGEX.test(value)) {
        payload.zips = handlePasteZipCodes(value, selectedLocations);
      } else {
        return;
      }

      try {
        event.preventDefault();

        setLoading(true);
        const { found, notFound } = await fetchTargetingLocationsLookup(payload);
        setLoading(false);

        setSelectedLocations([...selectedLocations, ...found]);
        if (notFound?.length && reportNotFoundLocations) {
          reportNotFoundLocations(notFound);
        }
      } catch (e) {
        toast.error('Failed to process pasted values.');
        console.error((e as Error)?.message);
        setLoading(false);
      }
    },
    [selectedLocations, setSelectedLocations, reportNotFoundLocations],
  );

  const hookData = useMemo(
    () => ({
      onValuesPaste,
      loading,
    }),
    [onValuesPaste, loading],
  );

  return hookData;
};

export default useGeoLocationsPaste;
